import { useState, DragEvent, ChangeEvent, ReactNode, useRef } from "react";
import { flushSync } from "react-dom";
import clsx from "clsx";
import { useSetAtom } from "jotai";
import axios, { AxiosError, type AxiosProgressEvent } from "axios";
import $api from "@/frontend/shared/api/axios";
import { NormalButton } from "@/frontend/shared/ui/Button";
import { UploadIcon } from "@/frontend/shared/assets/icons";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { fileProgresses, type TFileProgresses } from "@/frontend/atoms/data-room/upload";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import styles from "./DataroomFileContainer.module.scss";

type Props = {
  name: string;
  companyId: number;
  triggerRefetch: () => void;
  openModal: () => void;
  children: ReactNode;
};

export function DataroomFileContainer({ name, companyId, triggerRefetch, openModal, children }: Props) {
  const setFileProgresses = useSetAtom(fileProgresses);

  const [isDragging, setIsDragging] = useState(false);
  const [dragDepth, setDragDepth] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleDragEnter(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setDragDepth((prev) => prev + 1);
    setIsDragging(true);
  }

  function handleDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
  }

  function handleDragLeave(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setDragDepth((prev) => prev - 1);
    if (dragDepth === 0) {
      setIsDragging(false);
    }
  }

  async function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setDragDepth(0);
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    try {
      await uploadFiles(files);
    } catch (error) {
      throw new Error();
    }
  }

  async function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    try {
      await uploadFiles(files);
    } catch (error) {
      console.error("Error handling input change", error);
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }

  async function uploadFiles(files: File[]) {
    setIsLoading(true);

    const response = await $api.get<{ data: { url: string; token: string } }>("/me/uploads/bulk/url");
    const url = response?.data?.data?.url;
    const token = response?.data?.data?.token;
    if (!url || !token) {
      snackbarConstructor("Failed to upload file/s. Try again later or contact administrator.", "error");
      setIsLoading(false);
      return;
    }

    const _fileProgresses: TFileProgresses = {};
    for (const file of files) {
      _fileProgresses[file.name] = {
        progress: 0,
        status: "uploading"
      };
    }

    flushSync(() => {
      setFileProgresses(_fileProgresses);
      openModal();
    });

    const unixTimestamp = new Date().getTime();
    const promises = files.map(async (file) => {
      const filename = file.name;
      const cleanFilename = decodeURIComponent(filename)
        .replace(/[#()]/g, "")
        .replace(/\s+/g, "-")
        .trim()
        .toLowerCase();
      const newFilename = `${unixTimestamp}-${cleanFilename}`;
      const fileUrl = `${url}/${encodeURIComponent(newFilename)}`;
      const sasUploadUrl = `${fileUrl}?${token}`;

      try {
        const azureResponse = await axios.put(sasUploadUrl, file, {
          headers: {
            "x-ms-blob-type": "BlockBlob",
            "Content-Type": file.type
          },
          onUploadProgress: (progressEvent: AxiosProgressEvent) => {
            const percentCompleted = progressEvent?.total
              ? Math.round((progressEvent.loaded * 50) / progressEvent.total)
              : 1; // frontend illusion - J

            setFileProgresses((prev) => ({
              ...prev,
              [filename]: {
                progress: percentCompleted,
                status: "uploading",
                message: "Uploading file"
              }
            }));
          }
        });

        if (azureResponse.status !== 201) {
          setFileProgresses((prev) => ({
            ...prev,
            [filename]: {
              progress: 0,
              status: "errored",
              message: "Failed to upload file"
            }
          }));

          reportErrorToSentry(`Failed to upload ${filename} with Azure shared access signature`, sasUploadUrl);
          return;
        }
      } catch {
        setFileProgresses((prev) => ({
          ...prev,
          [filename]: {
            progress: 0,
            status: "errored",
            message: "Failed to upload file"
          }
        }));
        return;
      }

      try {
        await $api.post(
          "/founders/data-room/files/url",
          {
            blobPath: newFilename
          },
          {
            onUploadProgress: (progressEvent: AxiosProgressEvent) => {
              const percentCompleted = progressEvent?.total
                ? Math.round((progressEvent.loaded * 40) / progressEvent.total)
                : 1; // frontend illusion - J
              const progress = 49 + percentCompleted;
              setFileProgresses((prev) => ({
                ...prev,
                [filename]: {
                  progress,
                  status: "uploading",
                  message: "Scanning and processing file"
                }
              }));
            }
          }
        );

        setFileProgresses((prev) => ({
          ...prev,
          [filename]: {
            progress: 100,
            status: "completed",
            message: "Completed"
          }
        }));
      } catch (err) {
        const errorMessage = err instanceof AxiosError ? err?.response?.data.message : "Failed to upload file";

        setFileProgresses((prev) => ({
          ...prev,
          [filename]: {
            progress: 0,
            status: "errored",
            message: errorMessage ?? "Failed to upload file"
          }
        }));
      } finally {
        triggerRefetch();
      }
    });

    await Promise.all(promises);
    setIsLoading(false);
  }

  function handleButtonClick() {
    fileInputRef.current?.click();
  }

  return (
    <div
      className={clsx(styles.mainContainer, { [styles.hovering]: isDragging })}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className={clsx(styles.titleLeftContainer, isLoading && styles.disabled)}>
        <input
          multiple
          type="file"
          accept=".jpg,.jpeg,image/jpeg,.png,image/png,.pdf,application/pdf,.ppt,application/vnd.ms-powerpoint,.pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation,.doc,application/msword,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.txt,text/plain,.xls,application/vnd.ms-excel,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          id={name}
          ref={fileInputRef}
          className={styles.hidden}
          onChange={handleInputChange}
        />
        <label htmlFor={name} className={styles.UploadLabel}>
          <NormalButton
            onClick={handleButtonClick}
            className={styles.uploadButton}
            size="large"
            variant="secondary"
            isSubmitting={isLoading}
          >
            <UploadIcon className={styles.uploadIcon} />
            File Upload
          </NormalButton>
        </label>
      </div>

      {children}
    </div>
  );
}
